import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import HeroWelcome from "../components/HeroWelcome";
import Footer from "../components/Footer";
import api from '../api/axios';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import WppChat from "../components/whatsappchat"
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { motion } from 'framer-motion';

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sliderReady, setSliderReady] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await api.get('/products');
      const data = res.data;

      if (!data || !Array.isArray(data.data)) {
        throw new Error('Formato de datos inválido');
      }

      setProducts(data.data);
    } catch (err) {
      console.error('Error al obtener productos:', err);
      setError(err.message || 'Error al cargar productos');
      setProducts([]);
    } finally {
      setLoading(false);
      setTimeout(() => setSliderReady(true), 100);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // ✅ FLECHAS MEJORADAS - POSICIÓN CORRECTA
  const NextArrow = React.memo(({ onClick, currentSlide, slideCount }) => (
    <motion.button 
      onClick={onClick}
      disabled={currentSlide === slideCount - 1}
      className={`absolute -right-4 top-1/2 z-20 -translate-y-1/2 transform bg-[#83F4E9]/90 hover:bg-[#83F4E9] text-[#0C4B45] rounded-full p-3 shadow-lg border-2 border-white ${
        currentSlide === slideCount - 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      }`}
      whileHover={{ scale: currentSlide !== slideCount - 1 ? 1.1 : 1 }}
      whileTap={{ scale: currentSlide !== slideCount - 1 ? 0.9 : 1 }}
      aria-label="Siguiente producto"
    >
      <FiChevronRight size={20} />
    </motion.button>
  ));

  const PrevArrow = React.memo(({ onClick, currentSlide }) => (
    <motion.button 
      onClick={onClick}
      disabled={currentSlide === 0}
      className={`absolute -left-4 top-1/2 z-20 -translate-y-1/2 transform bg-[#83F4E9]/90 hover:bg-[#83F4E9] text-[#0C4B45] rounded-full p-3 shadow-lg border-2 border-white ${
        currentSlide === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      }`}
      whileHover={{ scale: currentSlide !== 0 ? 1.1 : 1 }}
      whileTap={{ scale: currentSlide !== 0 ? 0.9 : 1 }}
      aria-label="Producto anterior"
    >
      <FiChevronLeft size={20} />
    </motion.button>
  ));

  // ✅ CONFIGURACIÓN MEJORADA - PUNTOS CORREGIDOS
  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 5000,
    cssEase: "cubic-bezier(0.645, 0.045, 0.355, 1)",
    slidesToShow: 4,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    pauseOnHover: true,
    pauseOnFocus: true,
    pauseOnDotsHover: true,
    initialSlide: 0,
    lazyLoad: 'ondemand',
    
    // ✅ RESPONSIVE MEJORADO
    responsive: [
      {
        breakpoint: 1440,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          dots: true
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          dots: true
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true
        }
      }
    ],
    
    // ✅ SOLUCIÓN DEFINITIVA PARA LOS PUNTOS
    appendDots: dots => (
      <div style={{ 
        position: 'relative',
        marginTop: '40px',
        paddingTop: '20px',
        width: '100%'
      }}>
        <div style={{ 
          maxWidth: '100%', 
          margin: '0 auto', 
          padding: '0 16px' 
        }}>
          <ul style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            margin: 0,
            padding: 0,
            listStyle: 'none'
          }}>
            {dots}
          </ul>
        </div>
      </div>
    ),
    
    customPaging: i => (
      <button
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: '#d1d5db',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          outline: 'none'
        }}
        onMouseOver={(e) => e.target.style.backgroundColor = '#0C4B45'}
        onMouseOut={(e) => e.target.style.backgroundColor = '#d1d5db'}
        onFocus={(e) => {
          e.target.style.backgroundColor = '#0C4B45';
          e.target.style.boxShadow = '0 0 0 2px rgba(12, 75, 69, 0.3)';
        }}
        onBlur={(e) => {
          e.target.style.backgroundColor = '#d1d5db';
          e.target.style.boxShadow = 'none';
        }}
        aria-label={`Ir al slide ${i + 1}`}
      />
    )
  };

  if (loading) return (
    <motion.div 
      className="flex justify-center items-center min-h-screen bg-gradient-to-b from-[#0C4B45]/10 to-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="flex flex-col items-center"
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 5, -5, 0]
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          repeatType: 'reverse'
        }}
      >
        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] mb-4"></div>
        <span className="text-[#0C4B45] font-medium">Cargando productos...</span>
      </motion.div>
    </motion.div>
  );

  if (error) return (
    <div className="text-center py-20 bg-gradient-to-b from-[#0C4B45]/10 to-white">
      <motion.div 
        className="inline-block bg-white p-6 rounded-xl shadow-lg max-w-md mx-4"
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
      >
        <h3 className="text-xl font-bold text-[#662D8F] mb-2">Error al cargar productos</h3>
        <p className="text-[#0C4B45] break-words">{error}</p>
        <button 
          className="mt-4 px-4 py-2 bg-[#662D8F] text-white rounded-lg hover:bg-[#512577] transition-colors"
          onClick={fetchProducts}
          aria-label="Reintentar carga de productos"
        >
          Reintentar
        </button>
      </motion.div>
    </div>
  );

  return (
    <div className="home-page min-h-screen bg-gradient-to-b from-[#83F4E9]/10 via-white to-[#F2A9FD]/10">
      <HeroWelcome />

      {/* ✅ SECCIÓN CON ESPACIO PARA PUNTOS */}
      <section className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-10 flex flex-col items-center text-center sm:mb-12"
        >
          <span className="mb-3 rounded-full bg-[#83F4E9]/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#0C4B45]">Selección destacada</span>
          <h1 className="mb-3 text-3xl font-bold text-[#0C4B45] sm:text-4xl">
            Nuestros <span className="text-[#662D8F]">Productos</span>
          </h1>
          <div className="mb-4 h-1 w-16 rounded-full bg-gradient-to-r from-[#662D8F] to-[#F2A9FD]"></div>
          <p className="max-w-2xl text-sm text-[#0C4B45]/80 sm:text-base">
            Descubre nuestra exclusiva colección cuidadosamente seleccionada
          </p>
          <Link to="/products" className="mt-5 rounded-xl border border-[#0C4B45]/15 bg-white px-4 py-2 text-sm font-semibold text-[#0C4B45] shadow-sm transition hover:-translate-y-0.5 hover:border-[#0C4B45] hover:bg-[#0C4B45] hover:text-white">Ver todos los productos</Link>
        </motion.div>

        {/* ✅ CONTENEDOR PRINCIPAL */}
        <motion.div 
          className="relative w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          {products.length > 0 ? (
            <div className="relative w-full px-2 sm:px-4">
              {sliderReady && (
                <Slider {...sliderSettings} className="w-full">
                  {products.map((product) => (
                    <div key={product._id} className="px-2 sm:px-3 py-4 focus:outline-none">
                      <motion.div 
                        className="bg-white rounded-xl shadow-lg overflow-hidden h-full border border-[#83F4E9]/20 mx-auto max-w-xs sm:max-w-sm"
                        whileHover={{ 
                          y: -8,
                          boxShadow: "0 15px 20px -5px rgba(102, 45, 143, 0.1), 0 8px 8px -5px rgba(102, 45, 143, 0.04)"
                        }}
                        transition={{ duration: 0.3 }}
                        tabIndex="0"
                        aria-label={`Producto: ${product.name}`}
                      >
                        <ProductCard product={product} />
                      </motion.div>
                    </div>
                  ))}
                </Slider>
              )}
              
              {/* ✅ INDICADOR DE SCROLL PARA MÓVIL */}
              <div className="lg:hidden text-center mt-8">
                <span className="text-xs text-[#0C4B45]/60 bg-[#83F4E9]/20 px-3 py-1 rounded-full">
                  Desliza para ver más →
                </span>
              </div>
            </div>
          ) : (
            <motion.div 
              className="text-center py-12 bg-white rounded-xl shadow-sm max-w-md mx-auto"
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
            >
              <p className="text-[#0C4B45]/70 text-lg">No hay productos disponibles</p>
              <button 
                className="mt-4 px-4 py-2 bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] text-white rounded-lg hover:opacity-90 transition-opacity"
                onClick={fetchProducts}
                aria-label="Recargar productos"
              >
                Recargar
              </button>
            </motion.div>
          )}
        </motion.div>
      </section>
      
      <WppChat />
      <Footer />
    </div>
  );
};

export default React.memo(HomePage);
